import React from 'react';
import cx from 'classnames';

import { CrossCircleIcon, WarningIcon } from '@motorway/mw-highway-code';
import { INTENT, SIZE } from '@motorway/mw-highway-code/enums';
import { Intent } from '@motorway/mw-highway-code/src/types';
import { TippyProps } from '@tippy.js/react';

import { DEFAULT_TOOLTIP_Z_INDEX, Tooltip, tooltipPlacementMap, TooltipProps, TooltipThemes } from '../Tooltip/Tooltip';

import styles from './FeedbackTooltip.module.scss';

const feedbackTooltipMaxWidth = 350;
const intentList: Intent[] | undefined = [INTENT.ERROR, INTENT.WARNING];

type FeedbackTooltipProps = Partial<TooltipProps> & {
  content?: React.ReactNode;
  distance?: TippyProps['distance'];
  fillWidth?: boolean;
  intent?: Intent | null;
  isTabletMobile?: boolean;
  placementFlipped?: keyof typeof tooltipPlacementMap;
  theme?: typeof TooltipThemes[keyof typeof TooltipThemes];
  tooltipIcon?: React.ReactNode;
};

export const FeedbackTooltip = ({
  appendTo = () => document.body,
  children = null,
  className,
  content = null,
  distance,
  fillWidth = false,
  intent,
  interactive = true,
  isTabletMobile = false,
  maxWidth = feedbackTooltipMaxWidth,
  placement = 'right',
  placementFlipped = 'bottom',
  theme = TooltipThemes.LIGHT,
  tooltipIcon = null,
  zIndex = DEFAULT_TOOLTIP_Z_INDEX,
}: FeedbackTooltipProps) => {
  const renderIcon = () => {
    switch (intent) {
    case INTENT.ERROR:
      return <CrossCircleIcon filled={true} size={SIZE.XSMALL} />;
    case INTENT.WARNING:
      return <WarningIcon filled={true} size={SIZE.XSMALL} />;
    default:
      return null;
    }
  };

  const renderTooltipOverlay = (feedbackContent: React.ReactNode) => (
    <div className={styles['feedback-tooltip-content']}>
      {tooltipIcon || <div data-icon-color={intent}>{renderIcon()}</div>}
      <div className={styles.internal}>{feedbackContent}</div>
    </div>
  );

  const visible = Boolean(intent && intentList.includes(intent) && !!content);
  const sameWidthAsParent = isTabletMobile || fillWidth;

  const tooltipProps = {
    appendTo: sameWidthAsParent ? 'parent' : appendTo,
    bordered: true,
    className: cx(styles['feedback-tooltip'], className),
    distance,
    flip: false,
    flipBehavior: [placement, placementFlipped] as TippyProps['flipBehavior'],
    flipOnUpdate: true,
    interactive,
    maxWidth,
    overlay: renderTooltipOverlay(content),
    placement,
    tooltipTheme: theme,
    visible,
    zIndex,
  };

  return (
    <Tooltip {...tooltipProps}>
      {children}
    </Tooltip>
  );
};
